import { inject } from '@angular/core'
import { Router, type CanActivateFn, type CanMatchFn } from '@angular/router'
import { brandLanguageGuard } from '../i18n/pre-login-language'
import { HostContextService } from './host-context.service'

/**
 * FR-051 U7 (spec §3.6) — route gates by the host the app is served on. The host
 * kind comes only from the server (HostContextService); these gates never look
 * at `location.host` themselves.
 */

/** AC-4.2 / AC-4.6: the host is unknown or switched off. */
export const hostNotActiveMatch: CanMatchFn = () => inject(HostContextService).kind() === 'unknown'

/** The app is served on a live agency (brand owner's) web address. */
export const agencyHostMatch: CanMatchFn = () => inject(HostContextService).kind() === 'agency'

/**
 * FR-051 U7 fix F-A: the host context FAILED (after its one retry) — this may be an
 * agency host, so nothing platform-only may render: one neutral "temporarily
 * unavailable" page for every path.
 */
export const hostUnavailableMatch: CanMatchFn = () => inject(HostContextService).kind() === 'failed'

/**
 * AC-5.5 (web) + fix F-A (fail closed): the platform-only flows — the admin realm,
 * self-registration, the `/a/<name>` lookup — open only on a host the server
 * CONFIRMED is the platform's; never on an agency host and never after a failure.
 */
export const confirmedPlatformMatch: CanMatchFn = () => inject(HostContextService).isConfirmedPlatform()

/** The complement — the fail-closed branch of a platform-only mount (e.g. `/a/<name>`). */
export const notConfirmedPlatformMatch: CanMatchFn = () =>
  !inject(HostContextService).isConfirmedPlatform()

/**
 * AC-4.4 (plan F2): on an agency host the host beats the path — `/a/<x>/<rest>`
 * goes to the plain `/<rest>` (query + fragment kept), `/a/<x>` and `/a` to `/`.
 * `<x>` is never looked up, so nothing about it is revealed (FR-050 AC-6.5).
 */
export const hostBeatsPathGuard: CanActivateFn = (_route, state) => {
  const router = inject(Router)
  const tree = router.parseUrl(state.url)
  const segments = tree.root.children['primary']?.segments ?? []
  return router.createUrlTree(['/', ...segments.slice(2).map((s) => s.path)], {
    queryParams: tree.queryParams,
    fragment: tree.fragment ?? undefined,
  })
}

/**
 * Pre-login pages on an agency host speak the host brand's language, the same
 * rule as `/a/<name>` pages (FR-050 AC-8.2); elsewhere nothing changes.
 */
export const agencyHostLanguageGuard: CanActivateFn = (route, state) =>
  inject(HostContextService).isAgencyHost() ? brandLanguageGuard(route, state) : true
